import React from 'react';

/**
 * Options for {@link buildRadioRowSelection}.
 *
 * @typeParam T - The row record type rendered in the `ListView` table.
 */
export interface RadioRowSelectionOptions<T> {
  /** Currently selected row id (or `null` when nothing is selected). */
  selectedId: string | null;
  /** Called with the newly selected id, or `null` when cleared. */
  onSelectId: (id: string | null) => void;
  /** Resolves the unique selection key for a record (e.g. `r => r.id`). */
  getKey: (record: T) => string;
  /** Prefix for the (visually hidden) checkbox `name` attribute. */
  namePrefix: string;
}

/**
 * Builds the AntD `rowSelection` config shared by every records `ListView`
 * (Applications, Work Orders, Invoices). The list uses a single-select radio
 * model with the native selection column hidden — rows are selected by clicking
 * the row itself — so the checkbox cell is collapsed (`columnWidth: 0`,
 * `renderCell: () => null`) and the checkbox is `display: none`.
 *
 * Operation-specific column hooks call this inside their `useMemo` and supply
 * the record key + name prefix. Keeping the config in one place means a fix to
 * the selection behaviour applies to all records modules at once.
 */
export function buildRadioRowSelection<T>({
  selectedId,
  onSelectId,
  getKey,
  namePrefix
}: RadioRowSelectionOptions<T>) {
  return {
    type: 'radio' as const,
    selectedRowKeys: selectedId ? [selectedId] : [],
    onChange: (selectedRowKeys: React.Key[]) => {
      const id = selectedRowKeys[0] as string;
      onSelectId(id || null);
    },
    columnWidth: 0,
    hideSelectAll: true,
    preserveSelectedRowKeys: false,
    renderCell: () => null,
    getCheckboxProps: (record: T) => ({
      name: `${namePrefix}-${getKey(record)}`,
      style: { display: 'none' as const }
    })
  };
}

export default buildRadioRowSelection;
